
// Libraries
import { defineComponent, toRef, watch } from 'vue';

// Components
import SsoStepUpController from 'client/containers/user/SsoStepUp/SsoStepUpController.vue';

// Utils
import mfaFactorBuilder, { type MfaFactorBuilderOptions } from 'client/containers/user/MfaComponents/mfaUtils/mfaFactorBuilder';

// Services
import LocalStorageService from 'client/util/LocalStorageService';
import routerService from 'client/services/routerService';

// Store
import { mapStores } from 'pinia';
import UserLoginStore from 'client/stores/UserLoginStore';

// Constants & Types
import { LocalStorageConsts, LoginConsts } from 'client/util/Constants';
import { createSmsOtpLoginSession } from 'client/util/Constants/smsOtpLoginSession';
import MfaConstants from 'client/containers/Login/MfaConstants';

import type { MfaFactor } from 'client/types/Auth';
import type { SmsOtpLoginSessionPatch } from 'client/util/Constants/smsOtpLoginSession';

const {
  clientType,
} = LoginConsts;
const USER = clientType.user;

const { mfaStatus, mfaNames } = MfaConstants;
const { previousMfaMethod } = LocalStorageConsts;

export default defineComponent({
  name: 'SsoStepUpWrapper',

  data() {
    return {
      errorMessage: '',
      smsOtpLoginSession: createSmsOtpLoginSession(),
    };
  },

  props: {
    factorsRaw: {
      type: String,
      required: true,
    },
    mfaType: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const mfaType = toRef(props, 'mfaType');

    // AUTH-7610: Need to set factor before successful mfa attempt
    // because redirect happens server side
    watch(mfaType, (newValue) => {
      if (newValue && newValue !== mfaNames.sms_otp) {
        LocalStorageService.setItem(previousMfaMethod, newValue);
      }
    });
  },

  watch: {
    factorsRaw() {
      this.smsOtpLoginSession = createSmsOtpLoginSession();
    },
  },

  created() {
    this.userLoginStore.pageLoadUrl = window.location.href;
  },

  components: {
    SsoStepUpController,
  },

  computed: {
    ...mapStores(UserLoginStore),

    controllingQueryParams(): { factors?: string } {
      // this ensures we don't lose query params as we navigate
      const factorsRawParam = this.factorsRaw ? { factors: this.factorsRaw } : {};

      return {
        ...factorsRawParam,
      };
    },

    ssoMfaFactors(): MfaFactor[] {
      const built = this.getIdFirstFactors();

      if (!this.smsOtpLoginSession.removedFromChooser) {
        return built;
      }

      return built.filter((f) => f.name !== mfaNames.sms_otp);
    },

    factorsParsed(): { clientType: string, status: string, type: string }[] {
      return this.factorsRaw
        .split(',')
        .filter((factor) => factor)
        .map((factorName) => ({
          type: factorName,
          status: mfaStatus.available,
          clientType: this.clientType,
        }));
    },

    clientType() {
      return USER;
    },
  },

  methods: {
    getIdFirstFactors() {
      const mfa = { factors: this.factorsParsed };
      const { controllingQueryParams } = this;
      const options: MfaFactorBuilderOptions = {
        loginType: 'ssoStepUp',
        controllingQueryParams,
      };

      return mfaFactorBuilder(mfa, options);
    },

    patchSmsOtpLoginSession(patch: SmsOtpLoginSessionPatch) {
      const s = this.smsOtpLoginSession;

      if (patch.initialSendEverAttempted !== undefined) {
        s.initialSendEverAttempted = patch.initialSendEverAttempted;
      }

      if (patch.successfulSendIncrement) {
        s.successfulSendCount += patch.successfulSendIncrement;
      }

      if (patch.lastSessionId !== undefined) {
        s.lastSessionId = patch.lastSessionId;
      }

      if (patch.removedFromChooser !== undefined) {
        s.removedFromChooser = patch.removedFromChooser;
      }
    },

    handleTooManyFailedAttempts(__: any, errorMessage: string) {
      // will only reach this when calling `changeComponent`, indicating a 403 error
      routerService.goToUserLogin({
        params: { error: errorMessage },
      });
    },

    resetErrorMessage() {
      this.setErrorMessage('');
    },

    setErrorMessage(message: string) {
      this.errorMessage = message;
    },
  },
});
